import { Component, Input, OnInit } from '@angular/core';
import { InfiniteScrollCustomEvent } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { Inspeccion } from 'src/app/models/inspeccion';
import { Modelogeneraldatos } from 'src/app/models/modelogeneraldatos';
import { JavapiService } from 'src/app/services/javapi.service';
import { MetodosgenService } from 'src/app/services/metodosgen.service';
import { Metodosui } from 'src/app/services/metodosui';
import { LlenardatosComponent } from '../llenardatos/llenardatos.component';
import { InspecdatosareaComponent } from '../inspecdatosarea/inspecdatosarea.component';
import { InspeccioneditComponent } from '../inspeccionedit/inspeccionedit.component';
import { InspeccionareaeditComponent } from '../inspeccionareaedit/inspeccionareaedit.component';
import { AlertController, ModalController } from '@ionic/angular';
import { CotizacioneslistComponent } from '../cotizacioneslist/cotizacioneslist.component';
import { CotizacioneseditComponent } from '../cotizacionesedit/cotizacionesedit.component';
import { CotizacionesmenuComponent } from '../cotizacionesmenu/cotizacionesmenu.component';
import { InfomodalComponent, InfoItem, InfoSection } from '../infomodal/infomodal.component';

@Component({
  selector: 'app-inspeccionarea',
  templateUrl: './inspeccionarea.component.html',
  styleUrls: ['./inspeccionarea.component.scss'],
  standalone: false
})
export class InspeccionareaComponent implements OnInit {

  @Input() inspeccion!: Inspeccion;
  @Input() mode: 'view' | 'edit' = 'view';

  allDetalles: any[] = [];
  displayedDetalles: any[] = [];
  linkedCotizacion: any = null;
  loadingCotizacion: boolean = false;
  permissions: any = {};

  // Modos de vista para las áreas
  viewMode: 'list' | 'card' = 'card';
  currentCardIndex: number = 0;
  cardAnimation: 'left' | 'right' | null = null;
  private touchStartX: number = 0;
  private isChanged: boolean = false;

  constructor(
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController
  ) { }

  async ngOnInit() {
    this.permissions = this.metodosGenService.getPermissions();
    await this.getDetalles();
    await this.getLinkedCotizacion();
  }

  async getLinkedCotizacion() {
    this.loadingCotizacion = true;
    try {
      const res: Modelogeneraldatos[] = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "genModel", "getInspeccionCotizacion", String(this.inspeccion.id), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      ) || [];

      if (res && res.length > 0) {
        this.linkedCotizacion = {
          id: res[0].dt1,
          estado: res[0].dt5,
          total: Number(res[0].dt9) || Number(res[0].dt4)
        };
      } else {
        this.linkedCotizacion = null;
      }
    } catch (e) {
      console.error('Error fetching linked cotizacion:', e);
    } finally {
      this.loadingCotizacion = false;
    }
  }

  async vincularExistente() {
    const modal = await this.modalCtrl.create({
      component: CotizacioneslistComponent,
      componentProps: {
        selectionMode: true,
        parentType: 'solicitud',
        trabajo: {
          id: this.inspeccion.id,
          cliente: this.inspeccion.cliente,
          lugar: this.inspeccion.direccion, // Map direccion to lugar for Cotizacioneslist filter
          descripcion: this.inspeccion.descripcion
        }
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data && data.c_id) {
      // Vincular la cotización seleccionada a esta inspección en BD
      await this.saveVinculo(Number(data.c_id));
    } else if (data && data.linked) {
      // Ya fue vinculada desde el otro componente, solo refrescar
      await this.getLinkedCotizacion();
    }
  }

  async crearNueva() {
    // Open CotizacioneseditComponent with pre-filled data
    const modal = await this.metodosUi.openModal(CotizacioneseditComponent, {
      inspeccionId: this.inspeccion.id,
      inspeccionObj: this.inspeccion, // Pass the whole object for detail view
      clientePre: { id: this.inspeccion.cliente_id, nombre: this.inspeccion.cliente },
      direccionPre: { id: this.inspeccion.direccion_id, nombre: this.inspeccion.direccion },
      solicitantePre: { id: this.inspeccion.solicitante_id, nombre: this.inspeccion.solicitante },
      descripcionPre: `Cotización basada en Inspección #${this.inspeccion.id}`,
      itemsPre: this.allDetalles
        .filter(d => d.comentario && d.comentario.trim() !== '')
        .map(d => ({ 
          descripcion: d.comentario,
          areaId: d.id,
          areaObj: d
        }))
    });

    if (modal) {
      // Logic for automatic linking should be handled by CotizacioneseditComponent
      // But we refresh here anyway
      await this.getLinkedCotizacion();
    }
  }

  async saveVinculo(cotizacionId: number) {
    try {
      this.metodosUi.showLoading('Vinculando...');
      const payload = this.metodosGenService.setModeloGeneral(
        "genUpdate", "linkInspeccionCotizacion", String(this.inspeccion.id), String(cotizacionId), this.getUsuario(), 
        '', '', '', '', '', '', '', '', '', '', '', ''
      );

      const res = await firstValueFrom(this.javapiService.getCommonModel(payload));
      this.metodosUi.hideLoading();

      if (res) {
        this.metodosUi.showToast('Cotización vinculada con éxito', 'success');
        await this.getLinkedCotizacion();
      }
    } catch (e) {
      this.metodosUi.hideLoading();
      console.error('Error linking:', e);
      this.metodosUi.showToast('Error al vincular', 'danger');
    }
  }

  async irACotizacion() {
    const modal = await this.metodosUi.openModal(CotizacioneslistComponent, {
      trabajo: this.inspeccion,
      parentType: 'solicitud'
    });
    
    // Refresh to pick up any changes (like status) when coming back
    await this.getLinkedCotizacion();
  }

  async desvincular() {
    const confirm = await this.metodosUi.showConfirmation('Desvincular', '¿Estás seguro de desvincular esta cotización de la inspección?', 'Desvincular', true);
    if (confirm) {
      await this.saveVinculo(0);
    }
  }

  async getDetalles() {
    try {
      const listaTemp: Modelogeneraldatos[] = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "genModel", "inspeccionAreas", String(this.inspeccion.id), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      ) || [];

      // Map details (Areas)
      // id(dt1), Area(dt2), Estructura(dt3), Nota(dt4), Multi-Ids(dt7, dt8)
      this.allDetalles = listaTemp.map(item => ({
        id: Number(item.dt1),
        area: item.dt2 ?? '',
        estructura: item.dt3 ?? '',
        comentario: item.dt4 ?? '',
        dt1: item.dt1,
        dt2: item.dt2,
        dt6: item.dt6,
        dt7: item.dt7,
        dt8: item.dt8
      }));

      this.displayedDetalles = [...this.allDetalles];
      this.currentCardIndex = 0;

    } catch (e) {
      console.error('Error fetching details:', e);
    }
  }


  loadData(event: any) {
    this.displayedDetalles = this.metodosGenService.loadMore(this.allDetalles, this.displayedDetalles, 10);
    (event as InfiniteScrollCustomEvent).target.complete();
  }

  async addDetalle() {
    await this.openForm('add');
  }

  async editDetalle(detalle: any) {
    await this.openForm('edit', detalle);
  }

  async openDatos(area: any) {
    // Open Data View for this Area
    await this.metodosUi.openModal(InspecdatosareaComponent, {
      area: area,
      inspeccion: this.inspeccion
    });
  }

  async viewItemDetail(item: any) {
    if (!item.id) return;

    try {
      this.metodosUi.showLoading('Cargando resumen...', 'blue');
      
      const [medidasRes, estimadosRes]: [Modelogeneraldatos[], any[]] = await Promise.all([
        firstValueFrom(
          this.javapiService.getCommonModel(
            this.metodosGenService.setModeloGeneral(
              "genModel", "inspeccionMedidas", String(item.id), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
            )
          )
        ).catch(() => []),
        firstValueFrom(this.javapiService.getInspeccionEstimadosList(String(item.id))).catch(() => [])
      ]);

      this.metodosUi.hideLoading();

      const sections: InfoSection[] = [];

      // 1. Measures Summary (Separating M2 and ML)
      if (medidasRes && medidasRes.length > 0) {
        let totalM2 = 0;
        let totalML = 0;
        let totalUnid = 0;
        let unitM2 = '';
        let unitML = '';
        let unitUnid = '';

        const mappedItems = medidasRes.map(m => {
          const val = Number(m.dt6) || 0;
          const type = m.dt9; // lineal, cuadrada, unidad
          const unit = m.dt5 || '';

          if (type === 'cuadrada') { totalM2 += val; unitM2 = unit; }
          else if (type === 'lineal') { totalML += val; unitML = unit; }
          else { totalUnid += val; unitUnid = unit; }

          let detail = '';
          if (type === 'lineal') detail = `${m.dt8} x ${m.dt3}${unit}`;
          else if (type === 'unidad') detail = `${m.dt8} ${unit}`;
          else detail = `${m.dt8} x ${m.dt3}x${m.dt4}${unit}`;

          return {
            type: type,
            label: `${m.dt2} (${detail})`,
            value: `${val.toFixed(2)} ${unit}${type === 'cuadrada' ? '²' : ''}`,
            icon: type === 'lineal' ? 'trending-up-outline' : type === 'unidad' ? 'list-outline' : 'resize-outline'
          };
        });

        const m2Items: InfoItem[] = mappedItems.filter(m => m.type === 'cuadrada').map(({type, ...item}) => item);
        const mlItems: InfoItem[] = mappedItems.filter(m => m.type === 'lineal').map(({type, ...item}) => item);
        const unitItems: InfoItem[] = mappedItems.filter(m => m.type === 'unidad').map(({type, ...item}) => item);

        if (m2Items.length > 0) {
          sections.push({
            title: 'Medidas de Área (M²)',
            items: [
              ...m2Items,
              { label: 'TOTAL ÁREA (M²)', value: `${totalM2.toFixed(2)} ${unitM2}²`, valueClass: 'bold-value', icon: 'calculator-outline' }
            ]
          });
        }

        if (mlItems.length > 0) {
          sections.push({
            title: 'Medidas Lineales (ML)',
            items: [
              ...mlItems,
              { label: 'TOTAL LINEAL (ML)', value: `${totalML.toFixed(2)} ${unitML}`, valueClass: 'bold-value', icon: 'trending-up-outline' }
            ]
          });
        }

        if (unitItems.length > 0) {
          sections.push({
            title: 'Unidades y Otros',
            items: [
              ...unitItems,
              { label: 'TOTAL UNIDADES', value: `${totalUnid.toFixed(2)} ${unitUnid}`, valueClass: 'bold-value', icon: 'list-outline' }
            ]
          });
        }
      }

      // 2. Estimates Summary
      if (estimadosRes && estimadosRes.length > 0) {
        let totalEstimados = 0;
        const estimateItems: InfoItem[] = estimadosRes
          .filter(e => e.dt2 !== 'GENERAL') // Skip 'General' type as requested
          .map(e => {
            let cost = Number(e.dt8) || 0; // Use dt8 for cost (total calculated in DB or fix)
            
            // Fallback for old records where cost might be 0
            if (cost === 0 && e.dt2 === 'PLANILLA') {
              cost = (Number(e.dt6) || 0) * (Number(e.dt3) || 0) * (Number(e.dt4) || 0);
            } else if (cost === 0 && e.dt2 === 'MATERIAL') {
              cost = (Number(e.dt6) || 0) * (Number(e.dt7) || 0);
            }

            totalEstimados += cost;
            const label = e.dt2 === 'PLANILLA' 
              ? `Planilla: ${e.dt4} pers. x ${e.dt3} días` 
              : e.dt2 === 'MATERIAL' 
                ? `Material: ${e.dt7} unid. (${e.dt10 || ''})`
                : e.dt2 === 'MOVILIDAD'
                  ? `Movilidad: ${e.dt3} días x $${e.dt6}`
                  : e.dt2 === 'SUBCONTRATO'
                    ? `Subcontrato: ${e.dt9} (${e.dt10 || ''})`
                    : `${e.dt2}: ${e.dt10 || ''}`;
            
            return {
              label: label,
              value: `$${cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
              icon: 'cash-outline'
            };
          });

        if (estimateItems.length > 0) {
          sections.push({
            title: 'Costos estimados',
            items: [
              ...estimateItems,
              { label: 'TOTAL ESTIMADO', value: `$${totalEstimados.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, valueClass: 'bold-value', icon: 'wallet-outline' }
            ]
          });
        }
      }

      if (sections.length === 0) {
        this.metodosUi.showToast('No hay medidas ni estimados registrados para este ítem', 'warning');
        return;
      }

      await this.metodosUi.openModal(InfomodalComponent, {
        title: `Resumen: ${item.comentario || item.area || 'Ítem'}`,
        sections: sections
      });

    } catch (error) {
      this.metodosUi.hideLoading();
      console.error('Error viewing item detail:', error);
      this.metodosUi.showToast('Error al cargar el detalle', 'danger');
    }
  }

  async editInspeccion() {
    const modal = await this.metodosUi.openModal(InspeccioneditComponent, {
      mode: 'edit',
      inspeccionData: {
        dt1: this.inspeccion.id,
        dt2: this.inspeccion.cliente,
        dt3: this.inspeccion.direccion,
        dt4: this.inspeccion.descripcion,
        dt5: this.inspeccion.solicitante,
        dt6: this.inspeccion.estado,
        dt7: this.inspeccion.fecha_creacion,
        dt8: this.inspeccion.cliente_id,
        dt9: this.inspeccion.direccion_id,
        dt10: this.inspeccion.solicitante_id,
        dt13: this.inspeccion.incluye_material
      }
    });

    if (modal) {
      this.isChanged = true;
      // If modal returned data (newly added), update local state immediately for instant UX
      if (typeof modal === 'object') {
        this.inspeccion.incluye_material = modal.incluyeMaterial ? 'Y' : 'N';
      }
      // Still refresh from DB for full data integrity
      this.getInspeccionActualizada();
    }
  }

  async getInspeccionActualizada() {
    try {
      const res: Modelogeneraldatos[] = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "genModel", "inspeccionGetOne", String(this.inspeccion.id), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      ) || [];

      if (res && res.length > 0) {
        this.inspeccion = {
          id: Number(res[0].dt1),
          cliente: res[0].dt2,
          direccion: res[0].dt3,
          descripcion: res[0].dt4,
          solicitante: res[0].dt5,
          estado: res[0].dt6,
          fecha_creacion: res[0].dt7,
          incluye_material: res[0].dt13,
          cliente_id: Number(res[0].dt8),
          direccion_id: Number(res[0].dt9),
          solicitante_id: Number(res[0].dt10)
        };
      }
    } catch (e) {
      console.error('Error refreshing inspection:', e);
    }
  }

  async deleteInspeccion() {
    const confirm = await this.metodosUi.showConfirmation('Confirmar Eliminación', '¿Estás seguro de eliminar esta solicitud y todos sus datos relacionados?', 'Eliminar', true);
    if (!confirm) return;

    try {
      const payload = {
        trabajoD: { id: String(this.inspeccion.id) },
        trabajoP: { rutina: 'inspecciones', subrutina: 'delete' },
        items: [],
        usuario: this.getUsuario()
      };

      const res = await firstValueFrom(this.javapiService.insertaTrabajo(payload));
      if (res) {
        this.metodosUi.showToast('Solicitud eliminada', 'success');
        this.metodosUi.closeModal(true); // Close area modal with refresh trigger
      }
    } catch (e) {
      console.error('Error deleting:', e);
      this.metodosUi.showToast('Error al eliminar', 'danger');
    }
  }

  async openForm(mode: 'add' | 'edit', detalle?: any) {
    try {
      const saved = await this.metodosUi.openModal(InspeccionareaeditComponent, {
        mode: mode,
        areaData: detalle,
        inspeccionId: this.inspeccion.id
      });

      if (saved) {
        await this.getDetalles();
      }

    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error', 'danger');
    }
  }

  async saveArea(data: any, mode: 'add' | 'edit', id?: number) {
    // Logic to extract values
    const comps = data.components || {};
    const allComps: any[] = [];
    // Flatten comps
    for (const key in comps) {
      if (Array.isArray(comps[key])) allComps.push(...comps[key]);
    }
    if (Array.isArray(comps)) allComps.push(...comps);

    const getVal = (name: string) => {
      let found = allComps.find(c => c.nombre === name);
      if (!found) return '';
      // Handle objects/arrays?
      if (found.value && typeof found.value === 'object' && !Array.isArray(found.value)) return found.value.dt2 || found.value.nombre || found.value;
      return found.value; // Return array or string
    };

    const p_area = getVal('Area');
    const p_estructura = getVal('Estructura');
    const p_comentario = getVal('Comentario');

    const payload = {
      trabajoD: {
        id: mode === 'edit' && id ? String(id) : '0',
        cliente: p_area,
        lugar: p_estructura,
        descripcion: p_comentario,
        solicitante: String(this.inspeccion.id) // Link to Father
      },
      trabajoP: {
        rutina: 'inspeccion_areas',
        subrutina: mode === 'add' ? 'insert' : 'update'
      },
      items: [],
      usuario: this.getUsuario()
    };

    const res = await firstValueFrom(this.javapiService.insertaTrabajo(payload));

    if (res && (res.success || res.id)) {
      this.metodosUi.showToast('Ítem guardado', 'success');
      await this.getDetalles();
    }
  }

  async deleteArea(detalle: any) {
    const itemTitulo = detalle.comentario || detalle.area || 'este ítem';
    const confirm = await this.metodosUi.showConfirmation('Confirmar Eliminación', `¿Seguro de eliminar el ítem "${itemTitulo}"?`, 'Eliminar', true);
    if (!confirm) return;

    const payload = {
      trabajoD: { id: String(detalle.id) },
      trabajoP: { rutina: 'inspeccion_areas', subrutina: 'delete' },
      items: [],
      usuario: this.getUsuario()
    };
    await firstValueFrom(this.javapiService.insertaTrabajo(payload));
    this.metodosUi.showToast('Ítem eliminado', 'success');
    await this.getDetalles();
  }

  dismiss() {
    this.metodosUi.closeModal(this.isChanged);
  }

  private getUsuario(): string {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      return user.l_usuario || '';
    }
    return '';
  }

  // ── Lógica de la Vista Card de Áreas ─────────────────────────────────────
  setViewMode(mode: 'list' | 'card') {
    this.viewMode = mode;
    this.currentCardIndex = 0;
  }

  get currentCard(): any | null {
    if (this.displayedDetalles && this.displayedDetalles.length > 0) {
      return this.displayedDetalles[this.currentCardIndex];
    }
    return null;
  }

  get cardDots(): any[] {
    return new Array(this.displayedDetalles.length);
  }

  prevCard() {
    if (this.currentCardIndex > 0) {
      this.triggerCardAnimation('right');
      this.currentCardIndex--;
    }
  }

  nextCard() {
    if (this.currentCardIndex < this.displayedDetalles.length - 1) {
      this.triggerCardAnimation('left');
      this.currentCardIndex++;
    }
  }

  goToCard(index: number) {
    if (index >= 0 && index < this.displayedDetalles.length) {
      const direction = index > this.currentCardIndex ? 'left' : 'right';
      this.triggerCardAnimation(direction);
      this.currentCardIndex = index;
    }
  }

  isSmallDot(index: number): boolean {
    const diff = Math.abs(index - this.currentCardIndex);
    return diff >= 3;
  }

  onCardTouchStart(event: TouchEvent) {
    this.touchStartX = event.touches[0].clientX;
  }

  onCardTouchEnd(event: TouchEvent) {
    const touchEndX = event.changedTouches[0].clientX;
    const diffX = touchEndX - this.touchStartX;

    if (Math.abs(diffX) > 60) {
      if (diffX > 0) {
        this.prevCard();
      } else {
        this.nextCard();
      }
    }
  }

  private triggerCardAnimation(direction: 'left' | 'right') {
    this.cardAnimation = direction;
    setTimeout(() => {
      this.cardAnimation = null;
    }, 300);
  }

}
